import React, { Suspense, lazy, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './components/auth';
import { CommandPaletteProvider } from './components/ui/CommandPalette';
import { listProjects } from './services/projectsApi';
import { RouteErrorBoundary } from './components/ErrorBoundary';
import { analytics } from './services/analytics';

/** Track page views on route changes */
const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    analytics.page(location.pathname);
  }, [location.pathname]);
  return null;
};

// Lazy load components for better performance
const LandingPage = lazy(() => import('./components/LandingPage'));
const VisitorDashboard = lazy(() => import('./components/VisitorDashboard'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const PublicPortfolioView = lazy(() => import('./components/public/PublicPortfolioView'));
const PublicPortfolio = lazy(() => import('./components/portfolio/PublicPortfolio'));
const SharedExplainabilityView = lazy(() => import('./components/public/SharedExplainabilityView'));
const PrivacyPolicy = lazy(() => import('./components/legal/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/legal/TermsOfService'));
const SubscriptionSettings = lazy(() => import('./components/subscriptions/SubscriptionSettings'));
const ValuationPage = lazy(() => import('./components/valuation/ValuationPage'));
const ValuationList = lazy(() => import('./components/valuation/ValuationList'));
const TrustTransparency = lazy(() => import('./components/trust/TrustTransparency'));
const InviteAcceptPage = lazy(() => import('./components/onboarding/InviteAcceptPage'));

// Loading fallback component
export const LoadingFallback = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 via-primary-900 to-slate-900 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500 mx-auto mb-4"></div>
      <div className="text-white text-lg">Loading...</div>
    </div>
  </div>
);

/**
 * Protected Route wrapper
 * Redirects to landing if not authenticated
 */
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingFallback />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

/**
 * Role-based Dashboard selector
 * Shows Admin or Visitor dashboard based on user role
 * Fetches projects from database API when user is authenticated
 */
const DashboardRouter = ({ portfolioData: initialData }) => {
  const { user } = useAuth();
  const [portfolioData, setPortfolioData] = useState(initialData);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch projects from database API
  const loadProjectsFromAPI = useCallback(async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      const result = await listProjects(user, 500);
      const projects = result?.projects || [];

      // Transform to the format expected by dashboard components
      setPortfolioData({
        analyzed_projects: projects.map(p => ({
          name: p.name,
          description: p.description,
          language: p.language,
          stars: p.stars,
          forks: p.forks,
          topics: p.topics,
          id: p.id,
          has_analysis: p.has_analysis,
          scores: p.has_analysis ? { technical: 1 } : null, // Mark analyzed projects
        })),
        projects: projects,
      });
    } catch (error) {
      console.error('Failed to load projects from API:', error);
      // Fall back to initial data if API fails
      setPortfolioData(initialData || { analyzed_projects: [] });
    } finally {
      setIsLoading(false);
    }
  }, [user, initialData]);

  // Load projects when user is authenticated
  useEffect(() => {
    loadProjectsFromAPI();
  }, [loadProjectsFromAPI]);

  // Callback for when data is updated (e.g., after import)
  const handleDataUpdate = useCallback((newData) => {
    setPortfolioData(prev => ({ ...prev, ...newData }));
    // Refresh from API to get complete data
    loadProjectsFromAPI();
  }, [loadProjectsFromAPI]);

  // All authenticated users get the full dashboard with onboarding,
  // persona selection, and progressive feature disclosure.
  // VisitorDashboard is only for unauthenticated public portfolio views.
  return (
    <Suspense fallback={<LoadingFallback />}>
      <AdminDashboard
        portfolioData={portfolioData}
        onDataUpdate={handleDataUpdate}
        isLoading={isLoading}
      />
    </Suspense>
  );
};

/**
 * Main Router component
 * Defines all application routes
 */
const AppRouter = ({ portfolioData, onLoginClick, showLogin, setShowLogin }) => {
  const { user, loading } = useAuth();

  // Import LoginForm dynamically to avoid circular deps
  const LoginForm = React.lazy(() =>
    import('./components/auth').then(module => ({ default: module.LoginForm }))
  );

  return (
    <BrowserRouter>
      <RouteTracker />
      <CommandPaletteProvider>
        <RouteErrorBoundary>
        <Routes>
          {/* Legal pages - no auth required */}
        <Route
          path="/privacy"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <PrivacyPolicy />
            </Suspense>
          }
        />
        <Route
          path="/terms"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <TermsOfService />
            </Suspense>
          }
        />

        {/* Public share routes - no auth required */}
        <Route
          path="/share/:shareId"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <PublicPortfolioView />
            </Suspense>
          }
        />
        <Route
          path="/portfolio/:username"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <PublicPortfolioView />
            </Suspense>
          }
        />

        {/* Public portfolio pages - no auth required */}
        <Route
          path="/p/:slug"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <PublicPortfolio />
            </Suspense>
          }
        />

        {/* Shared explainability/audit views - no auth required (Phase 4) */}
        <Route
          path="/shared/explainability/:token"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <SharedExplainabilityView />
            </Suspense>
          }
        />
        <Route
          path="/shared/audit/:token"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <SharedExplainabilityView />
            </Suspense>
          }
        />
        <Route
          path="/shared-view"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <SharedExplainabilityView />
            </Suspense>
          }
        />

        {/* Trust & transparency - no auth required */}
        <Route
          path="/trust"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <TrustTransparency />
            </Suspense>
          }
        />

        {/* GitHub App callback - redirects after installation */}
        <Route
          path="/github/callback"
          element={
            <ProtectedRoute>
              <Navigate to="/dashboard" replace />
            </ProtectedRoute>
          }
        />

        {/* GitHub App post-installation setup - redirects to dashboard */}
        <Route
          path="/github/setup"
          element={
            <ProtectedRoute>
              <Navigate to="/dashboard?github_setup=complete" replace />
            </ProtectedRoute>
          }
        />

        {/* Login route */}
        <Route
          path="/login"
          element={
            user ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Suspense fallback={<LoadingFallback />}>
                <LoginForm
                  onSuccess={() => window.location.href = '/dashboard'}
                  onCancel={() => window.location.href = '/'}
                />
              </Suspense>
            )
          }
        />

        {/* Dashboard route - protected */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardRouter portfolioData={portfolioData} />
            </ProtectedRoute>
          }
        />

        {/* Subscription settings route - protected */}
        <Route
          path="/settings/subscription"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <div className="min-h-screen bg-gradient-to-br from-slate-900 via-primary-900 to-slate-900 p-8">
                  <div className="max-w-4xl mx-auto">
                    <SubscriptionSettings />
                  </div>
                </div>
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Valuation routes - protected */}
        <Route
          path="/valuation"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <div className="min-h-screen bg-gradient-to-br from-slate-900 via-primary-900 to-slate-900">
                  <ValuationList />
                </div>
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/valuation/new"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <div className="min-h-screen bg-gradient-to-br from-slate-900 via-primary-900 to-slate-900">
                  <ValuationPage />
                </div>
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/valuation/:assessmentId"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <div className="min-h-screen bg-gradient-to-br from-slate-900 via-primary-900 to-slate-900">
                  <ValuationPage />
                </div>
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Invite accept route - no auth required (shows login prompt if unauthenticated) */}
        <Route
          path="/invite/:token"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <InviteAcceptPage />
            </Suspense>
          }
        />

        {/* Root route - landing or dashboard based on auth */}
        <Route
          path="/"
          element={
            loading ? (
              <LoadingFallback />
            ) : showLogin && !user ? (
              <Suspense fallback={<LoadingFallback />}>
                <LoginForm
                  onSuccess={() => setShowLogin(false)}
                  onCancel={() => setShowLogin(false)}
                />
              </Suspense>
            ) : user ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Suspense fallback={<LoadingFallback />}>
                <LandingPage onLoginClick={onLoginClick} />
              </Suspense>
            )
          }
        />

        {/* Catch-all redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </RouteErrorBoundary>
      </CommandPaletteProvider>
    </BrowserRouter>
  );
};

export default AppRouter;
